import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Button, DatePicker } from '@/components';
import { SideBarModal } from '@/components/Modal';
import {
  extractApiErrorMessage,
  fileToBase64,
  getBucketUrlImageV2,
  openImageFileDialog,
  validateImageFile,
  uploadFileImage,
} from '@/utils';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';

/* ── Types ─────────────────────────────────────────────────────────── */

interface MarkCompletePayload {
  workCompleted?: string;
  actualCompletionAt?: string;
  completionImageURL?: string;
  completionNotes: string;
  isCostSameAsEstimate: boolean;
  actualCost: number;
  costVarianceReason?: string;
  tempSwapReturnAt?: string;
  permSwapReturnAt?: string;
}

interface TempFileData {
  name: string;
  size: number;
  base64?: string;
}

interface MaintenanceMarkCompleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: MarkCompletePayload) => void;
  isSubmitting?: boolean;
  estimatedCost?: number;
  approvalStatus?: string;
}

/* ── Style helpers ─────────────────────────────────────────────────── */

const CLOUD_ICON_PATH =
  'M3 15a4 4 0 004 4h10a4 4 0 000-8 5 5 0 00-9.7-1.7A4.5 4.5 0 003 15z';

const inputClassName = (hasError: boolean) =>
  `w-full rounded-md border px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 ${
    hasError
      ? 'border-red-500 focus:ring-red-200'
      : 'border-gray-200 focus:ring-purple-200'
  }`;

const hintClassName = (hasError: boolean) =>
  `mt-1 text-[11px] ${hasError ? 'text-red-500' : 'text-gray-400'}`;

const formatFileSize = (size?: number) => {
  if (!size && size !== 0) return '';
  if (size < 1024) return `${size} B`;
  const kb = size / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

const formatCurrency = (value: string) => {
  if (!value) return '';
  const num = Number(value);
  if (Number.isNaN(num)) return '';
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

/* ── Sub-components ────────────────────────────────────────────────── */

const CloudIcon: React.FC<{ className?: string }> = ({
  className = 'w-12 h-12',
}) => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    className={className}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d={CLOUD_ICON_PATH}
    />
  </svg>
);

const FieldLabel: React.FC<{
  children: React.ReactNode;
  required?: boolean;
  className?: string;
}> = ({ children, required, className = 'mb-1' }) => (
  <label className={`block text-xs font-medium text-gray-700 ${className}`}>
    {children}
    {required && <span className="text-red-500"> *</span>}
  </label>
);

const ErrorHint: React.FC<{ show: boolean; message?: string }> = ({
  show,
  message = 'Required',
}) =>
  show ? <div className="mt-1 text-[11px] text-red-500">{message}</div> : null;

interface ImageUploadZoneProps {
  isUploading: boolean;
  tempFile: TempFileData | null;
  hasError: boolean;
  isDragActive: boolean;
  onBrowse: () => void;
  onRemove: () => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
}

const ImageUploadZone: React.FC<ImageUploadZoneProps> = ({
  isUploading,
  tempFile,
  hasError,
  isDragActive,
  onBrowse,
  onRemove,
  onDragOver,
  onDragLeave,
  onDrop,
}) => (
  <>
    <div
      className={`border-2 border-dashed rounded-lg w-full ${
        hasError ? 'border-red-500' : 'border-gray-300'
      }`}
    >
      {isUploading ? (
        <div className="p-8 flex flex-col justify-center items-center text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
          <p className="mt-2 text-sm text-gray-600">Uploading document...</p>
        </div>
      ) : !tempFile ? (
        <div
          onClick={onBrowse}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          className={`p-8 flex flex-col justify-center items-center text-center cursor-pointer hover:bg-blue-50 transition-all ${
            isDragActive ? 'bg-blue-50' : ''
          }`}
        >
          <div className="text-gray-400 mb-4">
            <CloudIcon />
          </div>
          <p className="font-semibold text-gray-700">Add a completion photo</p>
          <p className="text-xs text-gray-500 mt-1">
            Image format, up to 10 MB.
          </p>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onBrowse();
            }}
            className="mt-4 bg-white border border-gray-300 text-gray-700 text-sm font-semibold py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Browse File
          </button>
        </div>
      ) : (
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="text-gray-600">
              <CloudIcon className="w-8 h-8" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">
                {tempFile.name || 'Completion Photo'}
              </p>
              <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                <span>{formatFileSize(tempFile.size)}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-green-500"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <span className="text-green-600 font-medium">Completed</span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onRemove}
            className="text-gray-500 hover:text-red-500 transition-colors"
          >
            <svg
              className="w-5 h-5 text-red-500 hover:text-red-700 cursor-pointer"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
    <ErrorHint show={hasError} />
  </>
);

/* ── Main component ────────────────────────────────────────────────── */

const MaintenanceMarkCompleteModal: React.FC<
  MaintenanceMarkCompleteModalProps
> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  estimatedCost,
  approvalStatus,
}) => {
  const [workCompleted, setWorkCompleted] = useState('');
  const [workCompletedTouched, setWorkCompletedTouched] = useState(false);
  const [actualCompletionAt, setActualCompletionAt] = useState('');
  const [completionNotes, setCompletionNotes] = useState('');
  const [permSwapReturnAt, setPermSwapReturnAt] = useState('');
  const [tempSwapReturnAt, setTempSwapReturnAt] = useState('');
  const [isCostSameAsEstimate, setIsCostSameAsEstimate] = useState<
    boolean | undefined
  >(undefined);
  const [actualCostInput, setActualCostInput] = useState('');
  const [costVarianceReason, setCostVarianceReason] = useState('');
  const [varianceReasonTouched, setVarianceReasonTouched] = useState(false);
  const [completionImageURL, setCompletionImageURL] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [tempFile, setTempFile] = useState<TempFileData | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  const parsedCost = actualCostInput ? Number(actualCostInput) : NaN;
  const actualCost = Number.isNaN(parsedCost) ? null : parsedCost;
  const formattedCost = useMemo(
    () => formatCurrency(actualCostInput),
    [actualCostInput]
  );

  // Reset all fields when modal closes
  useEffect(() => {
    if (!isOpen) {
      setWorkCompleted('');
      setWorkCompletedTouched(false);
      setActualCompletionAt('');
      setCompletionNotes('');
      setPermSwapReturnAt('');
      setTempSwapReturnAt('');
      setIsCostSameAsEstimate(undefined);
      setActualCostInput('');
      setCostVarianceReason('');
      setVarianceReasonTouched(false);
      setCompletionImageURL('');
      setIsUploading(false);
      setIsDragActive(false);
      setTempFile(null);
      setShowErrors(false);
    }
  }, [isOpen]);

  /* ── Image upload helpers ──────────────────────────────────────── */

  const uploadImage = async (fileData: TempFileData, base64Data?: string) => {
    const uuid = uuidv4();
    const response = await getBucketUrlImageV2({ type: 'maintenance', uuid });

    if (!response?.data) throw new Error('Failed to get upload URL');

    const payload = base64Data || fileData.base64?.split(',')[1];
    if (!payload) throw new Error('Invalid file data');

    const upload = await uploadFileImage(response.data, payload, fileData.name);
    if (upload.status !== 200) throw new Error('Upload failed');

    setCompletionImageURL(response.data);
    setTempFile(fileData);
    showSuccessToast('Success', 'Upload image success');
  };

  const handleUploadError = (error: unknown) => {
    showErrorToast(
      'Error',
      extractApiErrorMessage(error, 'Failed to upload image')
    );
    setCompletionImageURL('');
    setTempFile(null);
  };

  const handleBrowse = () => {
    openImageFileDialog(async (file) => {
      if (!file) return;
      setIsUploading(true);
      try {
        await uploadImage(file);
      } catch (error) {
        handleUploadError(error);
      } finally {
        setIsUploading(false);
      }
    });
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(false);

    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    if (!validateImageFile(file)) {
      showErrorToast('Error', 'Invalid image file');
      return;
    }

    setIsUploading(true);
    try {
      const base64 = await fileToBase64(file);
      await uploadImage({ name: file.name, size: file.size }, base64);
    } catch (error) {
      handleUploadError(error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setCompletionImageURL('');
    setTempFile(null);
  };

  /* ── Validation ────────────────────────────────────────────────── */

  const hasCostSameAnswer = isCostSameAsEstimate !== undefined;
  const workCompletedLength = workCompleted.trim().length;
  const varianceReasonLength = costVarianceReason.trim().length;
  const isPermSwap = approvalStatus === 'PERM_SWAP';
  const isTempSwap = approvalStatus === 'TEMP_SWAP';
  const isComplete = !isPermSwap && !isTempSwap;
  const shouldValidateWorkCompleted = isComplete || isTempSwap;

  const isCompletionDateValid = isComplete
    ? Boolean(actualCompletionAt)
    : isTempSwap
      ? Boolean(tempSwapReturnAt)
      : Boolean(permSwapReturnAt);

  const isFormValid =
    (!shouldValidateWorkCompleted || workCompletedLength >= 20) &&
    isCompletionDateValid &&
    (!isComplete || Boolean(completionImageURL)) &&
    hasCostSameAnswer &&
    (isCostSameAsEstimate === true || actualCost !== null) &&
    (isCostSameAsEstimate === true || varianceReasonLength >= 20);

  const errors = {
    workCompleted:
      shouldValidateWorkCompleted &&
      ((showErrors && workCompletedLength < 20) ||
        (workCompletedTouched &&
          workCompletedLength > 0 &&
          workCompletedLength < 20)),
    completionDate: showErrors && !isCompletionDateValid,
    completionImage: isComplete && showErrors && !completionImageURL,
    costSame: showErrors && !hasCostSameAnswer,
    actualCost:
      showErrors && isCostSameAsEstimate === false && actualCost === null,
    costVarianceReason:
      isCostSameAsEstimate === false &&
      ((showErrors && varianceReasonLength < 20) ||
        (varianceReasonTouched &&
          varianceReasonLength > 0 &&
          varianceReasonLength < 20)),
  };

  /* ── Submit ────────────────────────────────────────────────────── */

  const handleSubmit = () => {
    if (!isFormValid) {
      setShowErrors(true);
      return;
    }

    const fallbackCost = typeof estimatedCost === 'number' ? estimatedCost : 0;

    const notesValue = completionNotes.trim();

    onSubmit({
      workCompleted:
        isComplete || isTempSwap ? workCompleted.trim() : undefined,
      actualCompletionAt:
        isComplete || isTempSwap ? actualCompletionAt : undefined,
      completionImageURL: completionImageURL || undefined,
      completionNotes: notesValue,
      isCostSameAsEstimate: Boolean(isCostSameAsEstimate),
      actualCost: actualCost ?? fallbackCost,
      costVarianceReason:
        isCostSameAsEstimate === false ? costVarianceReason : undefined,
      permSwapReturnAt: isPermSwap ? permSwapReturnAt : undefined,
      tempSwapReturnAt: isTempSwap ? tempSwapReturnAt : undefined,
    });
  };

  /* ── Render ────────────────────────────────────────────────────── */

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={onClose}
      position="right"
      width="sm"
      maxHeight="screen"
      header={
        <div>
          <div className="text-base font-semibold text-gray-900">
            Mark Maintenance as Complete
          </div>
          <div className="text-xs text-gray-500">
            Provide final repair details and documentation to formally close
            this maintenance case
          </div>
        </div>
      }
      footer={
        <div className="flex w-full justify-end">
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting || isUploading}
            className="w-full"
            variant="primary"
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </div>
      }
      className="max-w-[400px]"
    >
      {isTempSwap && (
        <>
          <div className="bg-gray-50 px-4 py-2 text-[12px] font-medium text-gray-500 rounded-md">
            Temporary Vehicle
          </div>

          <div className="px-6 py-4 space-y-4">
            <FieldLabel required>Return Date & Time</FieldLabel>
            <DatePicker
              value={tempSwapReturnAt}
              onChange={setTempSwapReturnAt}
              showTimeSelect
              className="w-full"
              error={errors.completionDate}
              helperText={errors.completionDate ? 'Required' : undefined}
            />
          </div>
        </>
      )}
      <div className="bg-gray-50 px-4 py-2 text-[12px] font-medium text-gray-500 rounded-md">
        Detail
      </div>

      <div className="px-6 py-4 space-y-4">
        {(isComplete || isTempSwap) && (
          <>
            {/* Work Completed */}
            <div>
              <FieldLabel required>Work Completed</FieldLabel>
              <textarea
                value={workCompleted}
                onChange={(e) => {
                  setWorkCompletedTouched(true);
                  setWorkCompleted(e.target.value);
                }}
                placeholder="Describe the specific repairs performed"
                rows={4}
                className={inputClassName(errors.workCompleted)}
              />
              <div className={hintClassName(errors.workCompleted)}>
                Minimum 20 characters
              </div>
            </div>

            {/* Completion Date */}
            <div>
              <FieldLabel required>Actual Completion Date & Time</FieldLabel>
              <DatePicker
                value={actualCompletionAt}
                onChange={setActualCompletionAt}
                showTimeSelect
                className="w-full"
                error={errors.completionDate}
                helperText={errors.completionDate ? 'Required' : undefined}
              />
            </div>

            {/* Completion Photos */}
            <div>
              <FieldLabel required>Completion Photos</FieldLabel>
              <ImageUploadZone
                isUploading={isUploading}
                tempFile={tempFile}
                hasError={errors.completionImage}
                isDragActive={isDragActive}
                onBrowse={handleBrowse}
                onRemove={handleRemoveImage}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragActive(true);
                }}
                onDragLeave={() => setIsDragActive(false)}
                onDrop={handleDrop}
              />
            </div>
          </>
        )}

        {isPermSwap && (
          <div>
            <FieldLabel required>Permanent Swap Date & Time</FieldLabel>
            <DatePicker
              value={permSwapReturnAt}
              onChange={setPermSwapReturnAt}
              showTimeSelect
              className="w-full"
              error={errors.completionDate}
              helperText={errors.completionDate ? 'Required' : undefined}
            />
          </div>
        )}

        {/* Additional Notes */}
        <div>
          <FieldLabel>Additional Notes</FieldLabel>
          <textarea
            value={completionNotes}
            onChange={(e) => setCompletionNotes(e.target.value)}
            placeholder="Enter any additional observations or maintenance recommendations for this unit..."
            rows={3}
            className={inputClassName(false)}
          />
        </div>

        {/* Cost Same as Estimate */}
        <div>
          <FieldLabel required className="mb-2">
            Apakah Harga Final sama dengan Harga Estimasi di awal?
          </FieldLabel>
          <div
            className={`flex items-center gap-6 text-sm text-gray-700 rounded-md px-3 py-2 ${
              errors.costSame ? 'border-red-500' : ''
            }`}
          >
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="isCostSameAsEstimate"
                checked={isCostSameAsEstimate === true}
                onChange={() => setIsCostSameAsEstimate(true)}
                required
              />
              Yes
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="isCostSameAsEstimate"
                checked={isCostSameAsEstimate === false}
                onChange={() => setIsCostSameAsEstimate(false)}
                required
              />
              No
            </label>
          </div>
          <ErrorHint show={errors.costSame} />
        </div>

        {/* Conditional: Actual Cost + Reason (when "No") */}
        {isCostSameAsEstimate === false && (
          <>
            <div>
              <FieldLabel required>Actual Final Cost</FieldLabel>
              <div className="relative">
                <input
                  type="text"
                  inputMode="numeric"
                  value={formattedCost}
                  onChange={(e) =>
                    setActualCostInput(e.target.value.replace(/\D/g, ''))
                  }
                  placeholder="Rp 0"
                  className={inputClassName(errors.actualCost)}
                />
                <span className="absolute inset-y-0 right-3 flex items-center text-sm text-gray-400">
                  IDR
                </span>
              </div>
              <ErrorHint show={errors.actualCost} />
            </div>

            <div>
              <FieldLabel required>Reason</FieldLabel>
              <textarea
                value={costVarianceReason}
                onChange={(e) => {
                  setVarianceReasonTouched(true);
                  setCostVarianceReason(e.target.value);
                }}
                placeholder="Explain why the final cost differs from the estimate"
                rows={3}
                className={inputClassName(errors.costVarianceReason)}
              />
              <div className={hintClassName(errors.costVarianceReason)}>
                Minimum 20 characters
              </div>
            </div>
          </>
        )}
      </div>
    </SideBarModal>
  );
};

export default MaintenanceMarkCompleteModal;
